import { isHotkey as isHotkeyLib } from 'is-hotkey'
import type { HotKeyOptions, KeyboardEventLike } from 'is-hotkey'

/**
 * Detect whether the platform uses Meta (Mac/iOS) or Control as the primary modifier.
 *
 * `is-hotkey` checks `navigator.platform` at module load time, but WebKit on Linux
 * incorrectly reports `MacIntel` for `navigator.platform`. We use `navigator.userAgentData`
 * (when available) with a `navigator.userAgent` fallback for a more reliable check.
 */
const isMacPlatform = () => {
  if (typeof window === 'undefined') {
    return false
  }
  const uad = (navigator as unknown as Record<string, unknown>).userAgentData as
    | { platform?: string }
    | undefined
  if (uad?.platform) {
    return /mac|ios/i.test(uad.platform)
  }
  return /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)
}

const IS_MAC = isMacPlatform()
const MOD = IS_MAC ? 'meta' : 'control'

const resolveMod = (hotkey: string) => hotkey.replace(/\bmod\b/gi, MOD)

const resolveHotkeys = (hotkey: string | readonly string[]): string | readonly string[] =>
  Array.isArray(hotkey) ? (hotkey as string[]).map(resolveMod) : resolveMod(hotkey as string)

/** @note we wrap this to resolve `mod` with reliable platform detection */
export const isHotkey = (hotkey: string | readonly string[], options?: HotKeyOptions | undefined) =>
  isHotkeyLib(resolveHotkeys(hotkey), options) as (ev: KeyboardEventLike) => boolean
